import { CheckCircleFilled, CheckCircleOutlined, CloseCircleFilled } from "@ant-design/icons";
import { Button, Card, Radio, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vs } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import api from "../api/axios.js";
import { useEvent } from "../contexts/EventContext.jsx";
import { useView } from "../contexts/ViewContext.jsx";

import ActivityCard from "./ActivityCard.jsx";
import styles from "./PracticeQuiz.module.css";

const { Title } = Typography;


function PracticeQuiz({ learningJourneyId, view }) {
    const [quiz, setQuiz] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const { emitEvent } = useEvent();
    const { setOnAdvance } = useView();

    const armAdvance = () => {
        const viewId = view.view_id;
        setOnAdvance(async () => {
            try {
                await api.post(`/workspace/practice-quiz/mark-complete/`, {
                    learning_journey_id: learningJourneyId,
                    view_id: viewId,
                });
            } catch (error) {
                console.error("Failed to mark practice quiz complete:", error);
            }
        });
    };

    useEffect(() => {
        const data = view.view_data;
        setQuiz(data ? { question: data.question, body: data.body, ...data.quiz } : null);
        setSelectedOption(null);
        setSubmitting(false);
        setOnAdvance(null);
        // A reload after the quiz already resolved (but before the student clicked
        // next-view) must re-arm the same completion call -- the step hasn't advanced
        // yet, and view_schema() already reconstructed the resolved state above.
        if (data?.quiz?.resolved) {
            armAdvance();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, learningJourneyId, setOnAdvance]);

    // Unregister on unmount so a later, unrelated view's "Continue" click can never
    // fire this quiz's completion call.
    useEffect(() => () => setOnAdvance(null), [setOnAdvance]);

    const submitAnswer = async () => {
        if (!selectedOption) return;
        setSubmitting(true);
        try {
            const { data } = await api.post(`/workspace/practice-quiz/submit/`, {
                learning_journey_id: learningJourneyId,
                option_id: selectedOption,
            });
            setQuiz(prev => ({ ...prev, ...data }));
            if (data.resolved) {
                armAdvance();
            } else {
                setSelectedOption(null);
            }
            data.controls.forEach(control => emitEvent(control));
        } catch (error) {
            console.error("Failed to submit practice quiz answer:", error);
        } finally {
            setSubmitting(false);
        }
    };

    if (!quiz) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <p>Loading quiz...</p>
            </div>
        );
    }

    const locked = quiz.resolved || submitting;

    return (
        <ActivityCard view={view}>
            <Title level={4}>{quiz.question}</Title>

            {quiz.body && (
                <Card
                    style={{
                        margin: "10px 0",
                        padding: "10px",
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        color: "#4c4b4bff",
                        border: "none",
                        fontFamily: "sans-serif",
                    }}
                    styles={{ body: { padding: 0 } }}
                >
                    <ReactMarkdown
                        children={quiz.body}
                        remarkPlugins={[remarkGfm]}
                        components={{
                            code({ inline, className, children, ...props }) {
                                const match = /language-(\w+)/.exec(className || "");
                                return !inline && match ? (
                                    <SyntaxHighlighter
                                        {...props}
                                        style={vs}
                                        language={match[1]}
                                        PreTag="div"
                                        customStyle={{
                                            padding: "10px",
                                            borderRadius: "5px",
                                            overflowX: "auto",
                                            backgroundColor: "#f5f5f5",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                        }}
                                    >
                                        {String(children).replace(/\n$/, "")}
                                    </SyntaxHighlighter>
                                ) : (
                                    <code
                                        {...props}
                                        style={{
                                            backgroundColor: "#e8e8e8",
                                            padding: "2px 6px",
                                            borderRadius: "4px",
                                            fontFamily: "monospace",
                                            color: "#d6336c",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                        }}
                                    >
                                        {children}
                                    </code>
                                );
                            },
                        }}
                    />
                </Card>
            )}

            <Radio.Group
                onChange={(e) => setSelectedOption(e.target.value)}
                value={selectedOption}
                disabled={locked}
                style={{ width: "100%", marginBottom: "20px" }}
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    {quiz.options.map((option) => {
                        const isDisabled = option.display === "disabled";
                        const isSuccess = option.display === "success";
                        const pickedCorrectly = isSuccess && quiz.correct;

                        let icon = null;
                        if (pickedCorrectly) {
                            icon = <CheckCircleFilled className={styles.pickedCorrectIcon} />;
                        } else if (isSuccess) {
                            icon = <CheckCircleOutlined className={styles.revealedCorrectIcon} />;
                        } else if (isDisabled) {
                            icon = <CloseCircleFilled className={styles.pickedWrongIcon} />;
                        }

                        return (
                            <div key={option.id}>
                                <Radio
                                    value={option.id}
                                    size="large"
                                    disabled={locked || isDisabled}
                                    className={isDisabled ? styles.wrongOption : undefined}
                                    style={{
                                        transition: "all 0.3s ease",
                                        ...(isSuccess
                                            ? { color: "var(--success, #389e0d)", fontWeight: 600 }
                                            : {}),
                                    }}
                                >
                                    {icon} {option.text}
                                </Radio>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateRows: option.explanation ? "1fr" : "0fr",
                                        transition: "grid-template-rows 250ms ease",
                                        marginLeft: 28,
                                    }}
                                >
                                    <div style={{ overflow: "hidden" }}>
                                        <div
                                            style={{
                                                marginTop: 4,
                                                marginBottom: 4,
                                                padding: "6px 10px",
                                                borderRadius: 6,
                                                fontSize: 13,
                                                backgroundColor: isSuccess ? "#f6ffed" : "#fff7e6",
                                                color: isSuccess ? "#389e0d" : "#874d00",
                                            }}
                                        >
                                            {option.explanation}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Space>
            </Radio.Group>

            <Space style={{ marginTop: "10px" }}>
                <Button
                    className={styles.simpleButton}
                    onClick={submitAnswer}
                    disabled={
                        !selectedOption
                        || locked
                        || quiz.options.find(o => o.id === selectedOption)?.display === "disabled"
                    }
                >
                    Submit
                </Button>
            </Space>
        </ActivityCard>
    );
}

export default PracticeQuiz;
